import { apiRequest } from './client';
import { Submission, ReviewTask } from '../types';

export function listPendingCustomTasks(token: string) {
  return apiRequest<ReviewTask[]>('/tasks/custom-pending', { token });
}

export function resolveCustomTask(token: string, taskId: string, approve: boolean, note?: string) {
  return apiRequest<ReviewTask>(`/tasks/${taskId}/resolve-custom`, {
    method: 'PATCH',
    token,
    body: { approve, note: note || undefined },
  });
}

export function listOpenDisputes(token: string) {
  return apiRequest<Submission[]>('/submissions/disputes', { token });
}

export function resolveDispute(
  token: string,
  submissionId: string,
  forReviewer: boolean,
  adminNote?: string,
) {
  return apiRequest<Submission>(`/submissions/${submissionId}/resolve-dispute`, {
    method: 'PATCH',
    token,
    body: { forReviewer, adminNote: adminNote || undefined },
  });
}

export interface TaskReport {
  id: string;
  taskId: string;
  task?: ReviewTask;
  reporterId: string;
  reporter?: { id: string; name: string; email: string };
  reason: string;
  createdAt: string;
}

export function listTaskReports(token: string) {
  return apiRequest<TaskReport[]>('/tasks/reports', { token });
}

export interface WithdrawalRequest {
  id: string;
  userId: string;
  user?: { id: string; name: string; email: string };
  amountCoins: number;
  note?: string | null;
  createdAt: string;
  // Locked in at the moment the withdrawal was requested — always pay out to
  // these, not to whatever is currently on the user's profile.
  payoutBankNameSnapshot?: string | null;
  payoutAccountNumberSnapshot?: string | null;
  payoutAccountHolderNameSnapshot?: string | null;
}

export function listPendingWithdrawals(token: string) {
  return apiRequest<WithdrawalRequest[]>('/wallet/admin/pending-withdrawals', { token });
}

export function approveWithdrawal(token: string, id: string) {
  return apiRequest(`/wallet/admin/withdrawals/${id}/approve`, { method: 'PATCH', token });
}

export function rejectWithdrawal(token: string, id: string, reason?: string) {
  return apiRequest(`/wallet/admin/withdrawals/${id}/reject`, {
    method: 'PATCH',
    token,
    body: { reason: reason || undefined },
  });
}

export interface PendingTopup {
  id: string;
  userId: string;
  user?: { id: string; name: string; email: string };
  amountCoins: number;
  requestedAmountVnd?: number | null;
  note?: string | null;
  proofPhotoUrl?: string | null;
  createdAt: string;
}

export function listPendingTopups(token: string) {
  return apiRequest<PendingTopup[]>('/wallet/admin/pending-topups', { token });
}

export function getAdminTopupProofToken(token: string, transactionId: string) {
  return apiRequest<{ token: string }>(`/wallet/admin/topups/${transactionId}/photo-token`, { token });
}

export function approveTopup(token: string, id: string) {
  return apiRequest(`/wallet/admin/topups/${id}/approve`, { method: 'PATCH', token });
}

export function rejectTopup(token: string, id: string, reason?: string) {
  return apiRequest(`/wallet/admin/topups/${id}/reject`, {
    method: 'PATCH',
    token,
    body: { reason: reason || undefined },
  });
}

export function adminInstantTopup(token: string, userId: string, amountVnd: number) {
  return apiRequest('/wallet/admin/topups/instant', {
    method: 'POST',
    token,
    body: { userId, amountVnd },
  });
}

export interface CreditCoinsResult {
  id: string;
  amountCoins: number;
  creditedUser: { id: string; name: string; email: string };
}

/** Credits coins directly by the user's email or referral code — no VND
 * conversion, no effect on cumulativeTopupVnd/owner tier. */
export function adminCreditCoins(token: string, identifier: string, amountCoins: number, note?: string) {
  return apiRequest<CreditCoinsResult>('/wallet/admin/topups/credit-coins', {
    method: 'POST',
    token,
    body: { identifier, amountCoins, note: note || undefined },
  });
}

export interface AdminUserListItem {
  id: string;
  name: string;
  email: string;
  role: string;
  posterKind: string | null;
  hasReviewerRole: boolean;
  hasOwnerRole: boolean;
  coinBalance: number;
  identityVerified: boolean;
  suspendedAt: string | null;
  createdAt: string;
}

export interface AdminUserListResult {
  users: AdminUserListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminUserStats {
  totalRealUsers: number;
  reviewerCount: number;
  businessOwnerCount: number;
  individualPosterCount: number;
}

export function getUserStats(token: string) {
  return apiRequest<AdminUserStats>('/users/admin/user-stats', { token });
}

export function listUsers(token: string, params: { search?: string; role?: string; page?: number }) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.role) qs.set('role', params.role);
  if (params.page) qs.set('page', String(params.page));
  return apiRequest<AdminUserListResult>(`/users/admin/users?${qs.toString()}`, { token });
}

export interface AdminUserDetail extends AdminUserListItem {
  cumulativeTopupVnd: number;
  referralCode: string;
  tasksCreatedCount: number;
  phone: string | null;
  phoneVerified: boolean;
  emailVerified: boolean;
  identityVerificationStatus: string | null;
  vipExpiresAt: string | null;
  vipIsLifetime: boolean;
  ownerTier?: string;
  ownerEscrowFeePercent?: number;
  vipActive?: boolean;
  reviewerTier?: string;
  withdrawalFeePercent?: number;
  completedTaskCount?: number;
  rejectedTaskCount?: number;
  shopCount: number;
  submissionCount: number;
  taskCount: number;
  suspendedReason: string | null;
  suspendedById: string | null;
}

export function getUserDetail(token: string, id: string) {
  return apiRequest<AdminUserDetail>(`/users/admin/users/${id}`, { token });
}

export function suspendUser(token: string, id: string, reason: string) {
  return apiRequest<AdminUserDetail>(`/users/admin/users/${id}/suspend`, { method: 'PATCH', token, body: { reason } });
}

export function unsuspendUser(token: string, id: string) {
  return apiRequest<AdminUserDetail>(`/users/admin/users/${id}/unsuspend`, { method: 'PATCH', token });
}

/** Scrubs the account's PII (email/name/phone/CCCD/bank info/identity photo)
 * and suspends it permanently — fulfills a user's data-deletion request.
 * Irreversible. */
export function anonymizeUser(token: string, id: string) {
  return apiRequest<{ success: boolean }>(`/users/admin/users/${id}/anonymize`, { method: 'PATCH', token });
}

export interface DuplicateDeviceCluster {
  deviceFingerprint: string;
  users: { id: string; name: string; email: string; role: string; createdAt: string }[];
}

export function listDuplicateDevices(token: string) {
  return apiRequest<DuplicateDeviceCluster[]>('/users/admin/duplicate-devices', { token });
}

export interface FlaggedPhoto {
  id: string;
  url: string;
  submissionId: string;
  createdAt: string;
  submission?: {
    id: string;
    task?: { title: string; shop?: { name: string } };
    reviewer?: { id: string; name: string; email: string };
  };
  original?: {
    id: string;
    url: string;
    submission?: { reviewer?: { id: string; name: string; email: string } };
  } | null;
}

export function listFlaggedPhotos(token: string) {
  return apiRequest<FlaggedPhoto[]>('/submissions/admin/flagged-photos', { token });
}

export interface PendingIdentityVerification {
  id: string;
  name: string;
  email: string;
  role: string;
  identityIdNumber: string | null;
  identityPhotoUrl: string | null;
  createdAt: string;
}

export function listPendingIdentityVerifications(token: string) {
  return apiRequest<PendingIdentityVerification[]>('/users/admin/identity-verifications', { token });
}

export function resolveIdentityVerification(token: string, userId: string, approve: boolean, note?: string) {
  return apiRequest(`/users/admin/identity-verifications/${userId}/resolve`, {
    method: 'PATCH',
    token,
    body: { approve, note: note || undefined },
  });
}

/** Short-lived (5 min), single-purpose token for loading a specific user's
 * identity photo as an <Image> src — NOT the admin's session token. */
export function getAdminIdentityPhotoToken(token: string, userId: string) {
  return apiRequest<{ token: string }>(`/users/admin/identity-verifications/${userId}/photo-token`, { token });
}
